import { DefaultRootState } from 'react-redux';

import { Crop } from 'react-image-crop';
import { BannerImageCropType, ImageComponent, UpscaleStatus } from 'utils/constants/banner';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

type CampaignIdForBanner = string;
type BannerTitle = string;
type BannerText = string;
type BannerLink = string;
type BannerVacancyId = number | null;
type BannerId = number;
export type Images = Record<BannerImageCropType, Partial<ImageInfo>>;
type SmallImageAllowed = boolean;
type IsCropImageModalVisible = boolean;
type IsLoadedInCrop = boolean;
type IsCropsSaved = boolean;

export interface AdExtension {
    id: number;
    text: string;
}

interface CreatedCampaign {
    campaignId: number;
    name: string;
    vacancyId: number;
}

export interface CropInfo {
    crop: Crop;
    percentCrop: Crop;
}

export interface ImageInfoResponse {
    id: number;
    url: string;
    width: number;
    height: number;
    availableCropsForImage: BannerImageCropType[];
}
export interface AiImageQueuePosition {
    queuePosition: number;
    waitingTimeMs: number;
}

export interface ImageInfo {
    id: number;
    url: string;
    width: number;
    height: number;
    fileName: string;
    cropInfo: CropInfo;
    component: ImageComponent;
    isLoadedInCrop: IsLoadedInCrop;
    upscaleStatus?: UpscaleStatus;
    aiImageQueueId: number | null;
    aiImageQueuePosition: AiImageQueuePosition | null;
}

export interface BannerErrors {
    bannerTitle: string;
    bannerText: string;
    bannerLink: string;
    // todo будем сохранять в images ошибки валидации из bannerImages,
    //  но на фронте пока не выводим, нет дизайна https://jira.hh.ru/browse/HH-202099
    images: string;
    classificationCodeId: string;
}

interface BannerVacancy {
    areaName: string;
    foreignArea: boolean;
    name: string;
    url: string;
    vacancyId: number;
}

export interface BannerVideo {
    videoUploadId?: string;
    thumbnailUploadId?: string;
    width?: number;
    height?: number;
    videoUrl?: string;
    thumbnailUrl?: string;
}

interface BannerVideoFiles {
    video?: Blob;
    thumbnail?: Blob;
}

// ----------------------- actions -------------------------------------
const SET_CAMPAIGN_ID_FOR_BANNER = 'SET_CAMPAIGN_ID_FOR_BANNER';
const SET_CREATED_CAMPAIGNS = 'SET_CREATED_CAMPAIGNS';
const SET_BANNER_TITLE = 'SET_BANNER_TITLE';
const SET_BANNER_TEXT = 'SET_BANNER_TEXT';
const SET_BANNER_IMAGE_AI_IMAGE_QUEUE_ID_BY_TYPE = 'SET_BANNER_IMAGE_AI_IMAGE_QUEUE_ID_BY_TYPE';
const SET_BANNER_IMAGE_AI_IMAGE_QUEUE_POSITION_BY_TYPE = 'SET_BANNER_IMAGE_AI_IMAGE_QUEUE_POSITION_BY_TYPE';
const SET_BANNER_IMAGE_CROP_INFO_BY_TYPE = 'SET_BANNER_IMAGE_CROP_INFO_BY_TYPE';
const SET_BANNER_IMAGE_COMPONENT_BY_TYPE = 'SET_BANNER_IMAGE_COMPONENT_BY_TYPE';
const SET_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE = 'SET_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE';
const SET_BANNER_IMAGE_UPSCALE_STATUS_BY_TYPE = 'SET_BANNER_IMAGE_UPSCALE_STATUS_BY_TYPE';
const SET_BANNER_IMAGE_BY_TYPE = 'SET_BANNER_IMAGE_BY_TYPE';
const SET_BANNER_SAVED_IMAGE_BY_TYPE = 'SET_BANNER_SAVED_IMAGE_BY_TYPE';
const SET_BANNER_UPSCALED_IMAGE_BY_TYPE = 'SET_BANNER_UPSCALED_IMAGE_BY_TYPE';
const SET_BANNER_LINK = 'SET_BANNER_LINK';
const SET_BANNER_ERRORS = 'SET_BANNER_ERRORS';
const SET_BANNER_VACANCY_ID = 'SET_BANNER_VACANCY_ID';
const SET_BANNER_VACANCIES = 'SET_BANNER_VACANCIES';
const SET_BANNER_ID = 'SET_BANNER_ID';
const SET_BANNER_SMALL_IMAGE_ALLOWED = 'SET_BANNER_SMALL_IMAGE_ALLOWED';
const SET_IS_CROP_IMAGE_MODAL_VISIBLE = 'SET_IS_CROP_IMAGE_MODAL_VISIBLE';
const SET_IS_UPSCALE_TIP_VISIBLE = 'SET_IS_UPSCALE_TIP_VISIBLE';
const RESET_BANNER_INFO = 'RESET_BANNER_INFO';
const RESET_BANNER_IMAGES_TO_SAVED = 'RESET_BANNER_IMAGES_TO_SAVED';
const RESET_BANNER_IMAGE_TO_UPSCALED_BY_TYPE = 'RESET_BANNER_IMAGE_TO_UPSCALED_BY_TYPE';
const SET_BANNER_SAVED_TO_IMAGES = 'SET_BANNER_SAVED_TO_IMAGES';
const RESET_BANNER_IMAGES = 'RESET_BANNER_IMAGES';
const RESET_BANNER_UPSCALED_IMAGES = 'RESET_BANNER_UPSCALED_IMAGES';
const SET_IS_CROPS_SAVED = 'SET_IS_CROPS_SAVED';
const SET_BANNER_VIDEO = 'SET_BANNER_VIDEO';
const RESET_BANNER_VIDEO = 'RESET_BANNER_VIDEO';
const SET_BANNER_VIDEO_UPLOAD_ID = 'SET_BANNER_VIDEO_UPLOAD_ID';
const SET_BANNER_VIDEO_THUMBNAIL_UPLOAD_ID = 'SET_BANNER_VIDEO_THUMBNAIL_UPLOAD_ID';
const SET_BANNER_VIDEO_WIDTH = 'SET_BANNER_VIDEO_WIDTH';
const SET_BANNER_VIDEO_HEIGHT = 'SET_BANNER_VIDEO_HEIGHT';
const SET_BANNER_VIDEO_FILE = 'SET_BANNER_VIDEO_FILE';
const SET_BANNER_VIDEO_THUMBNAIL_FILE = 'SET_BANNER_VIDEO_THUMBNAIL_FILE';
const RESET_BANNER_VIDEO_FILES = 'RESET_BANNER_VIDEO_FILES';
const SET_IS_VIDEO_LOADING = 'SET_IS_VIDEO_LOADING';
const SET_CLASSIFICATION_CODE_ID = 'SET_BANNER_ERIR_CLASSIFICATION_CODE_ID';
const SET_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_AI_IMAGE_GENERATION_MODAL =
    'SET_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_AI_IMAGE_GENERATION_MODAL';
const SET_IS_ADVERTISEMENT = 'SET_IS_ADVERTISEMENT';
const SET_VACANCY_NAME = 'SET_VACANCY_NAME';

interface PayloadTypes {
    [SET_CAMPAIGN_ID_FOR_BANNER]: CampaignIdForBanner;
    [SET_CREATED_CAMPAIGNS]: CreatedCampaign[];
    [SET_BANNER_TITLE]: BannerTitle;
    [SET_BANNER_TEXT]: BannerText;
    [SET_BANNER_IMAGE_AI_IMAGE_QUEUE_ID_BY_TYPE]: { cropType: BannerImageCropType; aiImageQueueId: number | null };
    [SET_BANNER_IMAGE_AI_IMAGE_QUEUE_POSITION_BY_TYPE]: {
        cropType: BannerImageCropType;
        position: AiImageQueuePosition | null;
    };
    [SET_BANNER_IMAGE_CROP_INFO_BY_TYPE]: {
        cropType: BannerImageCropType;
        cropInfo: CropInfo;
    };
    [SET_BANNER_IMAGE_COMPONENT_BY_TYPE]: {
        cropType: BannerImageCropType;
        component: ImageComponent;
    };
    [SET_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE]: {
        cropType: BannerImageCropType;
        isLoadedInCrop: IsLoadedInCrop;
    };
    [SET_BANNER_IMAGE_UPSCALE_STATUS_BY_TYPE]: {
        cropType: BannerImageCropType;
        upscaleStatus?: UpscaleStatus;
    };
    [SET_BANNER_IMAGE_BY_TYPE]: { cropType: BannerImageCropType; imageInfo: Partial<ImageInfo> };
    [SET_BANNER_SAVED_IMAGE_BY_TYPE]: { cropType: BannerImageCropType; imageInfo: Partial<ImageInfo> };
    [SET_BANNER_UPSCALED_IMAGE_BY_TYPE]: { cropType: BannerImageCropType; imageInfo: Partial<ImageInfo> };
    [SET_BANNER_LINK]: BannerLink;
    [SET_BANNER_ERRORS]: Partial<BannerErrors>;
    [SET_BANNER_VACANCY_ID]: BannerVacancyId;
    [SET_BANNER_VACANCIES]: BannerVacancy[];
    [SET_BANNER_ID]: BannerId;
    [SET_BANNER_SMALL_IMAGE_ALLOWED]: SmallImageAllowed;
    [SET_IS_CROP_IMAGE_MODAL_VISIBLE]: IsCropImageModalVisible;
    [RESET_BANNER_INFO]: null;
    [RESET_BANNER_IMAGES_TO_SAVED]: null;
    [RESET_BANNER_IMAGE_TO_UPSCALED_BY_TYPE]: BannerImageCropType;
    [SET_BANNER_SAVED_TO_IMAGES]: null;
    [RESET_BANNER_IMAGES]: null;
    [RESET_BANNER_UPSCALED_IMAGES]: null;
    [SET_IS_CROPS_SAVED]: IsCropsSaved;
    [SET_IS_UPSCALE_TIP_VISIBLE]: boolean;
    [SET_BANNER_VIDEO]: BannerVideo | null;
    [RESET_BANNER_VIDEO]: null;
    [SET_BANNER_VIDEO_WIDTH]: number;
    [SET_BANNER_VIDEO_HEIGHT]: number;
    [SET_BANNER_VIDEO_UPLOAD_ID]: string;
    [SET_BANNER_VIDEO_THUMBNAIL_UPLOAD_ID]: string;
    [SET_BANNER_VIDEO_FILE]: Blob;
    [SET_BANNER_VIDEO_THUMBNAIL_FILE]: Blob;
    [RESET_BANNER_VIDEO_FILES]: null;
    [SET_IS_VIDEO_LOADING]: boolean;
    [SET_CLASSIFICATION_CODE_ID]: string | null;
    [SET_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_AI_IMAGE_GENERATION_MODAL]: boolean;
    [SET_IS_ADVERTISEMENT]: boolean;
    [SET_VACANCY_NAME]: string;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setCampaignIdForBanner = ActionCreator(SET_CAMPAIGN_ID_FOR_BANNER);
export const setCreatedCampaigns = ActionCreator(SET_CREATED_CAMPAIGNS);
export const setBannerTitle = ActionCreator(SET_BANNER_TITLE);
export const setBannerText = ActionCreator(SET_BANNER_TEXT);
export const setBannerImageAiImageQueueIdByType = ActionCreator(SET_BANNER_IMAGE_AI_IMAGE_QUEUE_ID_BY_TYPE);
export const setBannerImageAiImageQueuePositionByType = ActionCreator(SET_BANNER_IMAGE_AI_IMAGE_QUEUE_POSITION_BY_TYPE);
export const setBannerImageCropInfoByType = ActionCreator(SET_BANNER_IMAGE_CROP_INFO_BY_TYPE);
export const setBannerImageComponentByType = ActionCreator(SET_BANNER_IMAGE_COMPONENT_BY_TYPE);
export const setBannerImageIsLoadedInCropByType = ActionCreator(SET_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE);
export const setBannerImageUpscaleStatusByType = ActionCreator(SET_BANNER_IMAGE_UPSCALE_STATUS_BY_TYPE);
export const setBannerImageByType = ActionCreator(SET_BANNER_IMAGE_BY_TYPE);
export const setBannerSavedImageByType = ActionCreator(SET_BANNER_SAVED_IMAGE_BY_TYPE);
export const setBannerUpscaledImageByType = ActionCreator(SET_BANNER_UPSCALED_IMAGE_BY_TYPE);
export const setBannerLink = ActionCreator(SET_BANNER_LINK);
export const setBannerErrors = ActionCreator(SET_BANNER_ERRORS);
export const setBannerVacancyId = ActionCreator(SET_BANNER_VACANCY_ID);
export const setBannerVacancies = ActionCreator(SET_BANNER_VACANCIES);
export const setBannerId = ActionCreator(SET_BANNER_ID);
export const setBannerSmallImageAllowed = ActionCreator(SET_BANNER_SMALL_IMAGE_ALLOWED);
export const setIsCropImageModalVisible = ActionCreator(SET_IS_CROP_IMAGE_MODAL_VISIBLE);
export const resetBannerInfo = ActionCreator(RESET_BANNER_INFO);
export const resetBannerImagesToSaved = ActionCreator(RESET_BANNER_IMAGES_TO_SAVED);
export const resetBannerImageToUpscaledByType = ActionCreator(RESET_BANNER_IMAGE_TO_UPSCALED_BY_TYPE);
export const setBannerSavedToImages = ActionCreator(SET_BANNER_SAVED_TO_IMAGES);
export const resetBannerImages = ActionCreator(RESET_BANNER_IMAGES);
export const resetBannerUpscaledImages = ActionCreator(RESET_BANNER_UPSCALED_IMAGES);
export const setIsCropsSaved = ActionCreator(SET_IS_CROPS_SAVED);
export const setIsUpscaleTipVisible = ActionCreator(SET_IS_UPSCALE_TIP_VISIBLE);
export const setBannerVideo = ActionCreator(SET_BANNER_VIDEO);
export const resetBannerVideo = ActionCreator(RESET_BANNER_VIDEO);
export const setBannerVideoUploadId = ActionCreator(SET_BANNER_VIDEO_UPLOAD_ID);
export const setBannerVideoThumbnailUploadId = ActionCreator(SET_BANNER_VIDEO_THUMBNAIL_UPLOAD_ID);
export const setBannerVideoWidth = ActionCreator(SET_BANNER_VIDEO_WIDTH);
export const setBannerVideoHeight = ActionCreator(SET_BANNER_VIDEO_HEIGHT);
export const setBannerVideoFile = ActionCreator(SET_BANNER_VIDEO_FILE);
export const setBannerVideoThumbnailFile = ActionCreator(SET_BANNER_VIDEO_THUMBNAIL_FILE);
export const resetBannerVideoFiles = ActionCreator(RESET_BANNER_VIDEO_FILES);
export const setIsVideoLoading = ActionCreator(SET_IS_VIDEO_LOADING);
export const setClassificationCodeId = ActionCreator(SET_CLASSIFICATION_CODE_ID);
export const setIsCropImageModalWasOpenedFromAiImageGenerationModal = ActionCreator(
    SET_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_AI_IMAGE_GENERATION_MODAL
);
export const setIsAdvertisement = ActionCreator(SET_IS_ADVERTISEMENT);
export const setVacancyName = ActionCreator(SET_VACANCY_NAME);
// ----------------------- /actions -------------------------------------

// ----------------------- selectors -------------------------------------
export const selectIsAdvertisement = (state: DefaultRootState): boolean => state.createBanner.isAdvertisement;
// ----------------------- /selectors -------------------------------------

// ----------------------- reducer --------------------------------------
export interface CreateBannerState {
    campaignIdForBanner: CampaignIdForBanner;
    createdCampaigns: CreatedCampaign[];
    bannerTitle: BannerTitle;
    bannerText: BannerText;
    images: Images;
    savedImages: Images;
    upscaledImages: Images;
    bannerLink: BannerLink;
    bannerErrors: Partial<BannerErrors>;
    bannerVacancyId: BannerVacancyId;
    bannerVacancies: BannerVacancy[];
    bannerId: BannerId;
    smallImageAllowed: SmallImageAllowed;
    isCropImageModalVisible: IsCropImageModalVisible;
    isCropImageModalWasOpenedFromAiImageGenerationModal: boolean;
    isCropsSaved: IsCropsSaved;
    isUpscaleTipVisible: boolean;
    bannerVideo: BannerVideo | null;
    bannerVideoFiles: BannerVideoFiles;
    isVideoLoading: boolean;
    classificationCodeId: string | null;
    isAdvertisement: boolean;
    vacancyName: string;
}

const INIT_IMAGES_STATE = {
    component: ImageComponent.Input,
    upscaleStatus: undefined,
    aiImageQueueId: null,
    aiImageQueuePosition: null,
};

const INIT_IMAGES = {
    [BannerImageCropType.HH]: INIT_IMAGES_STATE,
    [BannerImageCropType.YandexAndVK]: INIT_IMAGES_STATE,
    [BannerImageCropType.VK]: INIT_IMAGES_STATE,
};

export const CREATE_BANNER_INITIAL_STATE: CreateBannerState = {
    campaignIdForBanner: '',
    createdCampaigns: [],
    bannerTitle: '',
    bannerText: '',
    images: INIT_IMAGES,
    savedImages: INIT_IMAGES,
    upscaledImages: INIT_IMAGES,
    bannerLink: '',
    bannerErrors: {},
    bannerVacancyId: null,
    bannerVacancies: [],
    bannerId: 0,
    smallImageAllowed: false,
    isCropImageModalVisible: false,
    isCropsSaved: false,
    isUpscaleTipVisible: true,
    bannerVideo: null,
    bannerVideoFiles: {},
    isVideoLoading: false,
    classificationCodeId: null,
    isCropImageModalWasOpenedFromAiImageGenerationModal: false,
    isAdvertisement: false,
    vacancyName: '',
};

export default createReducer<CreateBannerState, PayloadTypes>(CREATE_BANNER_INITIAL_STATE, {
    [SET_CREATED_CAMPAIGNS]: (state, { payload }) => ({
        ...state,
        createdCampaigns: payload,
    }),
    [SET_CAMPAIGN_ID_FOR_BANNER]: (state, { payload }) => ({
        ...state,
        campaignIdForBanner: payload,
    }),
    [SET_BANNER_TITLE]: (state, { payload }) => ({
        ...state,
        bannerTitle: payload,
    }),
    [SET_BANNER_TEXT]: (state, { payload }) => ({
        ...state,
        bannerText: payload,
    }),
    [SET_BANNER_IMAGE_AI_IMAGE_QUEUE_ID_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                aiImageQueueId: payload.aiImageQueueId,
            },
        },
    }),
    [SET_BANNER_IMAGE_AI_IMAGE_QUEUE_POSITION_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                aiImageQueuePosition: payload.position,
            },
        },
    }),
    [SET_BANNER_IMAGE_CROP_INFO_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                cropInfo: payload.cropInfo,
            },
        },
    }),
    [SET_BANNER_IMAGE_COMPONENT_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                component: payload.component,
            },
        },
    }),
    [SET_BANNER_IMAGE_IS_LOADED_IN_CROP_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                isLoadedInCrop: payload.isLoadedInCrop,
            },
        },
    }),
    [SET_BANNER_IMAGE_UPSCALE_STATUS_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: {
                ...state.images[payload.cropType],
                upscaleStatus: payload.upscaleStatus ?? undefined,
            },
        },
    }),
    [SET_BANNER_IMAGE_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload.cropType]: payload.imageInfo,
        },
    }),
    [SET_BANNER_SAVED_IMAGE_BY_TYPE]: (state, { payload }) => ({
        ...state,
        savedImages: {
            ...state.savedImages,
            [payload.cropType]: payload.imageInfo,
        },
    }),
    [SET_BANNER_UPSCALED_IMAGE_BY_TYPE]: (state, { payload }) => ({
        ...state,
        upscaledImages: {
            ...state.upscaledImages,
            [payload.cropType]: payload.imageInfo,
        },
    }),
    [SET_BANNER_LINK]: (state, { payload }) => ({
        ...state,
        bannerLink: payload,
    }),
    [SET_BANNER_ERRORS]: (state, { payload }) => ({
        ...state,
        bannerErrors: payload,
    }),
    [SET_BANNER_VACANCY_ID]: (state, { payload }) => ({
        ...state,
        bannerVacancyId: payload,
    }),
    [SET_BANNER_VACANCIES]: (state, { payload }) => ({
        ...state,
        bannerVacancies: payload,
    }),
    [SET_BANNER_ID]: (state, { payload }) => ({
        ...state,
        bannerId: payload,
    }),
    [SET_BANNER_SMALL_IMAGE_ALLOWED]: (state, { payload }) => ({
        ...state,
        smallImageAllowed: payload,
    }),
    [SET_IS_CROP_IMAGE_MODAL_VISIBLE]: (state, { payload }) => ({
        ...state,
        isCropImageModalVisible: payload,
    }),
    [RESET_BANNER_INFO]: (state) => ({
        ...state,
        ...CREATE_BANNER_INITIAL_STATE,
    }),
    [RESET_BANNER_IMAGES_TO_SAVED]: (state) => ({
        ...state,
        images: state.savedImages,
    }),
    [RESET_BANNER_IMAGE_TO_UPSCALED_BY_TYPE]: (state, { payload }) => ({
        ...state,
        images: {
            ...state.images,
            [payload]: state.upscaledImages[payload],
        },
    }),
    [SET_BANNER_SAVED_TO_IMAGES]: (state) => ({
        ...state,
        savedImages: state.images,
    }),
    [RESET_BANNER_IMAGES]: (state) => ({
        ...state,
        images: INIT_IMAGES,
    }),
    [RESET_BANNER_UPSCALED_IMAGES]: (state) => ({
        ...state,
        upscaledImages: INIT_IMAGES,
    }),
    [SET_IS_CROPS_SAVED]: (state, { payload }) => ({
        ...state,
        isCropsSaved: payload,
    }),
    [SET_IS_UPSCALE_TIP_VISIBLE]: (state, { payload }) => ({
        ...state,
        isUpscaleTipVisible: payload,
    }),
    [SET_BANNER_VIDEO]: (state, { payload }) => ({
        ...state,
        bannerVideo: payload,
    }),
    [RESET_BANNER_VIDEO]: (state) => ({
        ...state,
        bannerVideo: null,
    }),
    [SET_BANNER_VIDEO_UPLOAD_ID]: (state, { payload }) => ({
        ...state,
        bannerVideo: {
            ...state.bannerVideo,
            videoUploadId: payload,
        },
    }),
    [SET_BANNER_VIDEO_THUMBNAIL_UPLOAD_ID]: (state, { payload }) => ({
        ...state,
        bannerVideo: {
            ...state.bannerVideo,
            thumbnailUploadId: payload,
        },
    }),
    [SET_BANNER_VIDEO_WIDTH]: (state, { payload }) => ({
        ...state,
        bannerVideo: {
            ...state.bannerVideo,
            width: payload,
        },
    }),
    [SET_BANNER_VIDEO_HEIGHT]: (state, { payload }) => ({
        ...state,
        bannerVideo: {
            ...state.bannerVideo,
            height: payload,
        },
    }),
    [SET_BANNER_VIDEO_FILE]: (state, { payload }) => ({
        ...state,
        bannerVideoFiles: {
            ...state.bannerVideoFiles,
            video: payload,
        },
    }),
    [SET_BANNER_VIDEO_THUMBNAIL_FILE]: (state, { payload }) => ({
        ...state,
        bannerVideoFiles: {
            ...state.bannerVideoFiles,
            thumbnail: payload,
        },
    }),
    [RESET_BANNER_VIDEO_FILES]: (state) => ({
        ...state,
        bannerVideoFiles: {},
    }),
    [SET_IS_VIDEO_LOADING]: (state, { payload }) => ({
        ...state,
        isVideoLoading: payload,
    }),
    [SET_CLASSIFICATION_CODE_ID]: (state, { payload }) => ({
        ...state,
        classificationCodeId: payload,
    }),
    [SET_IS_CROP_IMAGE_MODAL_WAS_OPENED_FROM_AI_IMAGE_GENERATION_MODAL]: (state, { payload }) => ({
        ...state,
        isCropImageModalWasOpenedFromAiImageGenerationModal: payload,
    }),
    [SET_IS_ADVERTISEMENT]: (state, { payload }) => ({
        ...state,
        isAdvertisement: payload,
    }),
    [SET_VACANCY_NAME]: (state, { payload }) => ({
        ...state,
        vacancyName: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
